import React, { createContext, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageHeaderLayout from '../../../../components/Layout/PageHeaderLayout';
import { Box, Button, Card, Grid, Modal } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { ItemServices } from './ItemServices';
import dayjs from 'dayjs';
import Loading from '../../../../components/Loader/Loading';
import ItemForm from './ItemForm';
import { SectionServices } from '../Section/SectionServices';
import useResponsive from '../../../../hooks/useResponsive';
import GenerateAppUrl from './GenerateAppUrl';
import MobilePreview from './MobilePreview';
import { stringifyNumericDisplayOptions } from '../displayOptions';

const TAG_SERVICE_ROUTE = '/tagServicesScreen';

export const ItemDataContext = createContext();
const AddEditSectionItem = () => {
    const menuDispatch = useDispatch();
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const defaultSectionId = searchParams.get('sectionId') || '';
    const defaultPageType = searchParams.get('pageType') || 'section';
    useEffect(() => {
        menuDispatch({
            type: 'menuSelect',
            payload: defaultPageType === 'header' ? 'header-management' : defaultPageType === 'footer' ? 'footer-management' : 'sections'
        });
    }, [defaultPageType, menuDispatch]);
    const navigate = useNavigate();
    const { isSm } = useResponsive();
    const entityLabel = defaultPageType === 'header' ? 'Header Item' : defaultPageType === 'footer' ? 'Footer Item' : 'Section Item';
    const entityListLabel = defaultPageType === 'header' ? 'Header Items' : defaultPageType === 'footer' ? 'Footer Items' : 'Section Items';
    const [pageTitle, setPageTitle] = useState(`Add ${entityLabel}`);
    useEffect(()=>{
        if(id>0){
            setPageTitle(`Edit ${entityLabel}`);
            fetchItemData();
        }else{
            setPageTitle(`Add ${entityLabel}`);
        }
    },[id, entityLabel])
    const initialState = {
        title: '',
        subtitle: '',
        description: '',
        displayOptions: {
            showTitle: true,
            titlePosition: 'left',
            titleFontFamily: 'Default',
            titleFontWeight: 600,
            showSubTitle: true,
            subTitlePosition: 'left',
            subTitleFontFamily: 'Default',
            subTitleFontWeight: 400,
            showDescription: false,
            descriptionPosition: 'left',
            descriptionFontFamily: 'Default',
            descriptionFontWeight: 400,
            mediaType: 'image',
            cardWidth: '',
            cardHeight: '',
            marginTop: '',
            marginRight: '',
            marginBottom: '',
            marginLeft: '',
            paddingTop: '',
            paddingRight: '',
            paddingBottom: '',
            paddingLeft: '',
            badgeTextColor: '#FFFFFF'
        },
        imageUrl: null,
        thumbnailUrl: null,
        iconUrl: null,
        actionType: '',
        actionData: {},
        badgeText: '',
        badgeColor: '#FF6B35',
        badgeTextColor: '#FFFFFF',
        badgePosition: 'top-right',
        position: '',
        cardBackgroundColor: null,
        cardBorderColor: null,
        aspectRatio: '1:1',
        isActive: true,
        startDateTime: null,
        endDateTime: null,
        serviceRelations: [],
        sectionId: defaultSectionId ? parseInt(defaultSectionId) : ''
    };

    const [itemData, setItemData] = useState(initialState);
    const [sectionData, setSectionData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [openGenerateAppUrl, setOpenGenerateAppUrl] = useState(false);

    const ItemFormMethod = useForm({
        mode: 'onChange',
        defaultValues: initialState
    });

    const { handleSubmit, reset, setValue, watch, formState: { isDirty } } = ItemFormMethod;
    const fetchItemData = async () => {
        setIsLoading(true);
        try {
            const response = await ItemServices.getItemById(id);
            if (response.success == '1') {
                const data = response.data;
                const imageUrl = (data.imageUrl || '').toLowerCase();
                const inferredMediaType = /\.gif(?:[?#].*)?$/.test(imageUrl) ? 'gif' : 'image';
                const savedMediaType = data.displayOptions?.mediaType;
                const requiredData = {
                    sectionId: data.sectionId || '',
                    title: data.title || '',
                    subtitle: data.subtitle || '',
                    description: data.description || '',
                    displayOptions: {
                        ...initialState.displayOptions,
                        ...(data.displayOptions || {}),
                        mediaType: savedMediaType === 'gif' || savedMediaType === 'image'
                            ? savedMediaType
                            : inferredMediaType
                    },
                    imageUrl: data.imageUrl || null,
                    thumbnailUrl: data.thumbnailUrl || null,
                    iconUrl: data.iconUrl || null,
                    actionType: data.actionType === 'navigate' &&
                        data.actionData?.route === TAG_SERVICE_ROUTE
                        ? 'open_service'
                        : data.actionType || '',
                    actionData: data.actionData || {},
                    badgeText: data.badgeText || '',
                    badgeColor: data.badgeColor || '#FF6B35',
                    badgeTextColor: data.displayOptions?.badgeTextColor || '#FFFFFF',
                    badgePosition: data.badgePosition || 'top-right',
                    position: data.position || '',
                    cardBackgroundColor: data.cardBackgroundColor || null,
                    cardBorderColor: data.cardBorderColor || null,
                    aspectRatio: data.aspectRatio || '1:1',
                    isActive: data.isActive ?? true,
                    startDateTime: data.startDateTime ? dayjs(data.startDateTime).format('YYYY-MM-DDTHH:mm') : null,
                    endDateTime: data.endDateTime ? dayjs(data.endDateTime).format('YYYY-MM-DDTHH:mm') : null,
                    serviceRelations: (data.serviceRelations || []).map(relation => ({
                        id: relation.id,
                        servicesMasterId: relation.service?.parentServiceId || relation.servicesMasterId || '',
                        servicesChildId: relation.servicesMasterId || relation.service?.id || '',
                        categoryId: relation.category?.parentId || '',
                        subCategoryId: relation.serviceCategoryId || relation.category?.id || '',
                        serviceCategoryId: relation.serviceCategoryId || relation.category?.id || '',
                        categoryTitle: relation.category?.parentCategory?.title || '',
                        subCategoryTitle: relation.category?.title || '',
                        masterServiceTitle: relation.service?.parentService?.title || '',
                        childServiceTitle: relation.service?.title || ''
                    })) || []
                };
                setItemData(data);
                reset(requiredData, { isDirty: false });
            }
        } catch (error) {
            console.log("error", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch item details',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const [sectionList, setSectionList] = useState([]);
    const fetchSectionList = async () => {
        try{
            const response = await SectionServices.fetchSectionList({
                pageType: defaultPageType,
                sortBy: 'position',
                sortType: 'ASC'
            });
            if(response.success == '1'){
                const sections = response.data.list || [];
                setSectionList(sections);
            }else if(response.success == '0'){
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.message || 'Failed to fetch sections',
                });
                setSectionList([]);
            }

        } catch(error){
            console.log("error", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch section details',
            });
        }
    }

    const sectionId = watch('sectionId');
    const backSectionId = sectionId || itemData?.sectionId || defaultSectionId;
    const sectionItemsBackPath = backSectionId
        ? `/customer-app/home-screen/section-items/${backSectionId}?pageType=${defaultPageType}`
        : `/customer-app/home-screen/section-items?pageType=${defaultPageType}`;

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        const startDate = data.startDateTime ? new Date(data.startDateTime) : null;
        const endDate = data.endDateTime ? new Date(data.endDateTime) : null;
        if (startDate && endDate && endDate < startDate) {
            setIsSubmitting(false);
            Swal.fire({
                icon: 'error',
                title: 'Invalid Date Range',
                text: 'End date/time must be after start date/time.'
            });
            return;
        }
        if (data.isActive !== false && endDate && endDate < new Date()) {
            setIsSubmitting(false);
            Swal.fire({
                icon: 'error',
                title: 'Invalid End Date',
                text: 'End date/time cannot be in the past for an active item. Please update or clear the end date/time.'
            });
            return;
        }
        const getActionData = () => {
            if (data.actionType === 'open_service') {
                return { route: TAG_SERVICE_ROUTE };
            }
            if (data.actionType === 'navigate') {
                return { route: data.actionData?.route || '' };
            }
            if (data.actionType === 'open_url') {
                return { url: data.actionData?.url || '' };
            }
            return null;
        };
        const shouldSendServiceRelations = data.actionType === 'open_service';
        const serviceRelations = shouldSendServiceRelations
            ? (data.serviceRelations || []).map(relation => ({
                serviceCategoryId: relation.subCategoryId || relation.serviceCategoryId,
                servicesMasterId: relation.servicesChildId || relation.servicesMasterId || null,
                id: relation.id || 0
            })).filter(relation => relation.serviceCategoryId)
            : [];
        if (shouldSendServiceRelations && serviceRelations.length === 0) {
            setIsSubmitting(false);
            Swal.fire({
                icon: 'error',
                title: 'Tag Service Required',
                text: 'Please add at least one service for this action'
            });
            return;
        }

        const modifiedData = {
            sectionId: parseInt(sectionId),
            title: data.title,
            subtitle: data.subtitle || null,
            description: data.description || null,
            displayOptions: {
                ...stringifyNumericDisplayOptions(data.displayOptions),
                badgeTextColor: data.badgeText ? data.badgeTextColor : null
            },
            imageUrl: data.imageUrl || null,
            thumbnailUrl: data.thumbnailUrl || null,
            iconUrl: data.iconUrl || null,
            actionType: data.actionType === 'open_service' ? 'navigate' : data.actionType,
            actionData: getActionData(),
            badgeText: data.badgeText || null,
            badgeColor: data.badgeText ? data.badgeColor : null,
            badgePosition: data.badgeText ? data.badgePosition : null,
            position: data.position ? parseInt(data.position) : 0,
            cardBackgroundColor: data.cardBackgroundColor || null,
            cardBorderColor: data.cardBorderColor || null,
            aspectRatio: data.aspectRatio,
            isActive: data.isActive,
            startDateTime: data.startDateTime ? new Date(data.startDateTime).toISOString() : null,
            endDateTime: data.endDateTime ? new Date(data.endDateTime).toISOString() : null,
            serviceRelations
        };

        try {
            if (id>0) {
                const updateResponse = await ItemServices.updateItem(id, modifiedData);
                if (updateResponse.success == '1') {
                    await Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: `${entityLabel} updated successfully`,
                    });
                    navigate(`/customer-app/home-screen/section-item/view/${id}?sectionId=${data.sectionId || ''}&pageType=${defaultPageType}`);
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed',
                        text: updateResponse.message || `${entityLabel} update failed`,
                    });
                }
            } else {
                const createResponse = await ItemServices.addItem(modifiedData);
                if (createResponse.success == '1') {
                    const result = await Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: `${entityLabel} created successfully`,
                        showCancelButton: true,
                        confirmButtonText: 'Add New',
                        cancelButtonText: 'Show List',
                        confirmButtonColor: 'green',
                        cancelButtonColor: '#5856d6',
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                    });
                    if (result.isConfirmed) {
                        reset(initialState);
                        setItemData(initialState);
                    }
                    if (result.dismiss === Swal.DismissReason.cancel) {
                        navigate(`/customer-app/home-screen/section-items/${data.sectionId}?pageType=${defaultPageType}`);
                    }
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed',
                        text: createResponse.message || `${entityLabel} creation failed`,
                    });
                }
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Failed',
                text: id ? `${entityLabel} update failed` : `${entityLabel} creation failed`,
                timer: 1500
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        fetchSectionList();
    }, []);
  return (
    <>
      <PageHeaderLayout headerTitle={pageTitle}>
            <Button sx={{textTransform:'none'}} size='small' variant='contained' startIcon={<ArrowBack/>} onClick={()=>navigate(sectionItemsBackPath)}>
                Back to {entityListLabel}
            </Button>
        </PageHeaderLayout>
        <ItemDataContext.Provider value={{ itemData, setItemData, isSubmitting, onSubmit, sectionList }}>
            {(isLoading || isSubmitting) && <Loading />}
            {/* <PageHeaderLayout headerTitle={id ? `Edit Item - ${sectionData?.title || 'Section'}` : `Add New Item - ${sectionData?.title || 'Section'}`}>
                <Button variant='contained' onClick={() => navigate(-1)} sx={{ height: "30px", marginTop: "10px" }} startIcon={<ArrowBack />}>
                    Back
                </Button>
            </PageHeaderLayout> */}
            <FormProvider {...ItemFormMethod}>
                <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2}>
                        <ItemForm sectionData={sectionData} setOpenGenerateAppUrl={setOpenGenerateAppUrl} />
                        <Grid item xs={12} md={4}>
                            <Box sx={{ position: 'sticky', top: 20 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    sx={{ mb: 1 }}
                                    disabled={!isDirty || isSubmitting}
                                >
                                    {id>0 ? `Update ${entityLabel}` : `Create ${entityLabel}`}
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => navigate(-1)}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <MobilePreview />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </FormProvider>
            {/* ================ Generate App Url Modal =============== */}
            <Modal
                open = {openGenerateAppUrl}
                disableEnforceFocus
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                sx={{ 
                    overflow: "scroll",
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)'
                    },
                    '& .MuiBox-root': {
                        outline: 'none'  // Remove the focus outline
                    }
                    
                }}
            >
                <Box 
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        height:isSm? '100vh':'auto',
                        borderRadius:'1rem',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'background.paper',
                        // border: '2px solid #ddd',
                        // boxShadow: 24,
                        // p: 2,
                    }}
                    width={(isSm) ? "95%" : "50%"}
                >
                    <Card>
                        <GenerateAppUrl
                            onSelectUrl={(appUrl, actionType = 'navigate') => {
                                ItemFormMethod.setValue('actionType', actionType, { shouldDirty: true, shouldValidate: true });
                                ItemFormMethod.setValue(
                                    'actionData',
                                    actionType === 'none' ? null : { route: appUrl },
                                    { shouldDirty: true, shouldValidate: true }
                                );
                                setOpenGenerateAppUrl(false);
                            }}
                            onClose={()=>{
                                setOpenGenerateAppUrl(false);
                            }}
                        />
                    </Card>
                </Box>
            </Modal>
        </ItemDataContext.Provider>
    </>
  )
}

export default AddEditSectionItem
